import React, { useMemo } from "react";
import PropTypes from "prop-types";
import {
  Checkbox,
  Chip,
  FormControl,
  FormHelperText,
  InputAdornment,
  ListItemText,
  TextField,
  makeStyles,
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import SearchIcon from "@material-ui/icons/Search";
import A_FormHelper from "../../../../Atoms/DataDisplay/Tooltips/A_FormHelper";

const useStyles = makeStyles((theme) => ({
  formControl: {
    marginTop: 0,
    "& .MuiInputLabel-root": {
      color: "var(--color-text-secondary)",
      "&.Mui-focused": {
        color: "var(--color-primary-main)",
      },
      "&.Mui-error": {
        color: "#f44336 !important",
      },
    },
    "& .MuiInputBase-root": {
      color: "var(--color-text-primary) !important",
      "&::before": {
        borderBottomColor: "var(--total_row_boldbackground)",
      },
      "&:hover:not(.Mui-disabled):before": {
        borderBottomColor: "var(--color-primary-main)",
      },
      "&::after": {
        borderBottomColor: "var(--color-primary-main)",
      },
    },
    "& .MuiFormHelperText-root": {
      color: "var(--color-text-secondary)",
      "&.Mui-error": {
        color: "#f44336 !important",
      },
    },
  },
  autocomplete: {
    "& .MuiAutocomplete-inputRoot": {
      paddingBottom: "4px !important",
    },
    "& .MuiAutocomplete-tag": {
      backgroundColor: "rgba(128, 128, 128, 0.1)",
      color: "var(--color-text-primary)",
      border: "1px solid var(--total_row_boldbackground)",
      "& .MuiChip-deleteIcon": {
        color: "var(--color-text-secondary)",
      },
    },
    "& .MuiAutocomplete-popupIndicator": {
      color: "var(--color-text-secondary)",
      'html[data-mode="dark"] &': {
        color: "var(--color-text-primary) !important",
      },
    },
    "& .MuiAutocomplete-clearIndicator": {
      color: "var(--color-text-secondary)",
      'html[data-mode="dark"] &': {
        color: "var(--color-text-primary) !important",
      },
    },
  },
  paper: {
    backgroundColor: "var(--color-background-paper) !important",
    color: "var(--color-text-primary) !important",
    boxShadow: "0 8px 16px rgba(0,0,0,0.4) !important",
    border: "1px solid var(--total_row_boldbackground)",
  },
  option: {
    fontSize: 13,
    "&[aria-selected='true']": {
      backgroundColor: "rgba(51, 122, 183, 0.15) !important",
    },
    "&[data-focus='true']": {
      backgroundColor: "rgba(128, 128, 128, 0.1) !important",
    },
    "&:hover": {
      backgroundColor: "rgba(128, 128, 128, 0.05) !important",
    },
  },
  searchIcon: {
    color: "var(--color-text-secondary)",
    marginRight: theme.spacing(0.5),
  },
}));

const coerceToArray = (value) => {
  if (value == null || value === "") return [];
  if (Array.isArray(value)) return value;
  return [value];
};

const findOptionByValue = (options, value) =>
  options.find((o) => `${o?.value}` === `${value}`);

const normalizeSelectedOption = (options, value) => {
  if (value && typeof value === "object" && "value" in value) {
    return value;
  }
  return findOptionByValue(options, value) || null;
};

const normalizeSelectedOptions = (options, value) =>
  coerceToArray(value)
    .map((v) => normalizeSelectedOption(options, v))
    .filter(Boolean);

const LookupSearchSelect = ({
  id,
  label,
  options,
  defaultValue,
  multiple,
  required,
  disabled,
  error,
  helperText,
  fieldHelperText,
  onChange,
  renderOption,
  fullWidth,
}) => {
  const classes = useStyles();
  const selectedValue = useMemo(() => {
    if (multiple) return normalizeSelectedOptions(options, defaultValue);
    return normalizeSelectedOption(options, defaultValue);
  }, [defaultValue, multiple, options]);

  return (
    <FormControl
      error={error}
      fullWidth={fullWidth}
      className={classes.formControl}
    >
      <Autocomplete
        id={id}
        multiple={multiple}
        disableCloseOnSelect={multiple}
        options={options}
        value={selectedValue}
        disabled={disabled}
        size="small"
        className={classes.autocomplete}
        classes={{
          paper: classes.paper,
          option: classes.option,
        }}
        getOptionLabel={(option) => option?.label || ""}
        getOptionSelected={(option, value) =>
          `${option?.value}` === `${value?.value}`
        }
        onChange={(_event, newValue) => onChange(newValue, id)}
        renderTags={(value, getTagProps) =>
          (value || []).map((option, index) => (
            <Chip
              {...getTagProps({ index })}
              key={`${id}-tag-${option?.value ?? index}`}
              size="small"
              label={option?.label || ""}
              variant="outlined"
            />
          ))
        }
        renderOption={
          renderOption ||
          ((option, { selected }) =>
            multiple ? (
              <>
                <Checkbox
                  color="primary"
                  checked={selected}
                  className={classes.searchIcon}
                />
                <ListItemText
                  primary={option?.label || ""}
                  classes={{ primary: classes.option }}
                />
              </>
            ) : (
              option?.label || ""
            ))
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            variant="standard"
            margin="dense"
            size="small"
            required={required}
            error={error}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <>
                  <InputAdornment position="start">
                    <SearchIcon
                      className={classes.searchIcon}
                      fontSize="small"
                    />
                  </InputAdornment>
                  {params.InputProps.startAdornment}
                </>
              ),
              endAdornment: (
                <>
                  <A_FormHelper fieldHelperText={fieldHelperText} />
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
            onKeyDown={(e) => e.stopPropagation()}
          />
        )}
      />
      <FormHelperText>{helperText}</FormHelperText>
    </FormControl>
  );
};

LookupSearchSelect.defaultProps = {
  options: [],
  defaultValue: "",
  multiple: false,
  required: false,
  disabled: false,
  error: false,
  helperText: "",
  fieldHelperText: "",
  renderOption: null,
  fullWidth: true,
};

LookupSearchSelect.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.any,
    })
  ),
  defaultValue: PropTypes.any,
  multiple: PropTypes.bool,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  fieldHelperText: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  renderOption: PropTypes.func,
  fullWidth: PropTypes.bool,
};

export default React.memo(LookupSearchSelect);
